import {
	Alert,
	Box,
	Chip,
	Divider,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import StatCard from "@components/StatCard";
import { useEffect, useState } from "react";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { ApiRequestFailed, authenticateAndFetchData, retryFn } from "@lib/apis";
import { useQuery } from "@tanstack/react-query";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { Link } from "react-router-dom";
import { CustomCellRendererProps } from "ag-grid-react";
import { urls } from "@routes";
import { format } from "date-fns";
import { ReportProblem, MarkEmailUnread, LocalFireDepartment, Whatshot } from "@mui/icons-material";
import { Downloading } from "@mui/icons-material";
import { useSnackbar } from "notistack";

interface BounceRecord {
	environment: string;
	organization: { name: string | null; permalink: string | null };
	server: { name: string | null; permalink: string | null };
	id: number;
	token: string;
	rfc_message_id: string | null;
	recipient: string;
	sender: string;
	subject: string | null;
	status: string;
	event_type: string;
	traffic_type: string;
	timestamp: number;
	timestamp_ms: number;
	bounce_dsn_id: number | null;
	code: string | null;
	output: string | null;
	details: string | null;
	workspace_id: number | null;
	workspace_name: string | null;
	user_id: number | null;
	user_email: string | null;
	reason_type: string;
	reason_code: string;
	reason: string;
}

interface BounceTotals {
	bounced?: number;
	hard_fail?: number;
	campaign?: number;
	warmup?: number;
	test?: number;
}

interface ServerError {
	server: string;
	error: string;
}

interface PaginationData {
	data: BounceRecord[];
	total_count: number;
	total_pages: number;
	current_page: number;
	page_size: number;
}

type EnvironmentFilter = "all" | "prod" | "staging" | "dev";
type EventTypeFilter = "all" | "bounced" | "hard_fail";
type TrafficTypeFilter = "all" | "campaign" | "warmup" | "test";

export default function AdminPostalBounces() {
	const theme = useTheme();
	const { enqueueSnackbar } = useSnackbar();

	const [pageData, setPageData] = useState<PaginationData>();
	const [totals, setTotals] = useState<BounceTotals>({});
	const [serverErrors, setServerErrors] = useState<ServerError[]>([]);
	const [postalUrl, setPostalUrl] = useState("");
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState("");
	const [environment, setEnvironment] = useState<EnvironmentFilter>("all");
	const [eventType, setEventType] = useState<EventTypeFilter>("all");
	const [trafficType, setTrafficType] = useState<TrafficTypeFilter>("all");
	const [days, setDays] = useState(7);
	const [exportLoading, setExportLoading] = useState(false);

	const adminPostalBouncesQuery = useQuery({
		queryKey: [
			"adminPostalBouncesQuery",
			page,
			pageSize,
			search,
			environment,
			eventType,
			trafficType,
			days,
		],
		queryFn: () =>
			authenticateAndFetchData(
				`/admin/postal-bounces/?page=${page}&page_size=${pageSize}&search=${encodeURIComponent(
					search
				)}&environment=${environment}&event_type=${eventType}&traffic_type=${trafficType}&days=${days}`
			),
		gcTime: 0,
		refetchOnWindowFocus: false,
		retry: retryFn,
	});

	useEffect(() => {
		if (adminPostalBouncesQuery.data?.data) {
			const response = adminPostalBouncesQuery.data.data;

			// Postal fetches one page's worth of rows from every mail server, so reachable
			// depth is bounded. Clamp the control rather than offering pages the API rejects.
			const maxPage = response.max_page ?? response.total_pages;

			setPageData({
				data: response.data || [],
				total_count: response.total_count,
				total_pages: Math.min(response.total_pages, maxPage),
				current_page: response.current_page,
				page_size: response.page_size,
			});
			setTotals(response.totals || {});
			setServerErrors(response.errors || []);
			setPostalUrl(response.postal_url || "");
		}
	}, [adminPostalBouncesQuery.data]);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setPage(1);
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	function handleEnvironmentChange(
		_event: React.MouseEvent<HTMLElement>,
		newValue: string | null
	) {
		if (newValue) {
			setEnvironment(newValue as EnvironmentFilter);
			setPage(1);
		}
	}

	const RecipientLink = (cellProps: CustomCellRendererProps) => {
		const orgPermalink = cellProps.data["organization"]?.permalink;
		const serverPermalink = cellProps.data["server"]?.permalink;

		if (!cellProps.value) {
			return <>---</>;
		}
		if (!postalUrl || !orgPermalink || !serverPermalink) {
			return <>{cellProps.value}</>;
		}
		return (
			<Tooltip title={"View this message in Postal"}>
				<a
					href={`${postalUrl}/org/${orgPermalink}/servers/${serverPermalink}/messages/${cellProps.data["id"]}`}
					target={"_blank"}
					rel={"noopener noreferrer"}
					style={{ color: theme.palette.text.primary }}>
					{cellProps.value}
				</a>
			</Tooltip>
		);
	};

	const UserDetailsLink = (cellProps: CustomCellRendererProps) => {
		if (!cellProps.data["user_id"] || !cellProps.value) {
			return <>---</>;
		}
		return (
			<Link
				to={urls["adminUserDetails"].replace(":userId", cellProps.data["user_id"])}
				style={{ color: theme.palette.text.primary }}>
				{cellProps.value}
			</Link>
		);
	};

	function environmentChipColor(environment: string) {
		switch (environment) {
			case "prod":
				return "error";

			case "staging":
				return "warning";

			default:
				return "default";
		}
	}

	const EnvironmentBadge = (params: CustomCellRendererProps) => {
		if (!params.value) {
			return <>---</>;
		}
		return (
			<Chip size={"small"} color={environmentChipColor(params.value)} label={params.value} />
		);
	};

	function trafficTypeChipColor(trafficType: string) {
		switch (trafficType) {
			case "warmup":
				return "warning";

			case "test":
				return "info";

			default:
				return "default";
		}
	}

	const TrafficTypeBadge = (params: CustomCellRendererProps) => {
		return (
			<Chip size={"small"} color={trafficTypeChipColor(params.value)} label={params.value} />
		);
	};

	const EventTypeBadge = (params: CustomCellRendererProps) => {
		return (
			<Chip
				size={"small"}
				color={params.value === "MessageBounced" ? "error" : "warning"}
				label={params.value === "MessageBounced" ? "Bounced" : "Delivery Failed"}
			/>
		);
	};

	const OutputCell = (params: CustomCellRendererProps) => {
		const text = params.value || params.data["details"] || "";
		if (!text) {
			return <>---</>;
		}
		return (
			<Tooltip title={text}>
				<span>{text}</span>
			</Tooltip>
		);
	};

	function timestampFormatter(params: ValueFormatterParams) {
		if (!params.value) {
			return "---";
		}
		return format(new Date(params.value), "do MMM y, h:mm a");
	}

	function emptyValueFormatter({ value }: ValueFormatterParams) {
		return value === null || value === undefined || value === "" ? "---" : value;
	}

	const handleExportClick = async () => {
		setExportLoading(true);

		try {
			const response = await authenticateAndFetchData(
				`/admin/postal-bounces/export/?search=${encodeURIComponent(search)}&environment=${environment}&event_type=${eventType}&traffic_type=${trafficType}&days=${days}`
			);

			enqueueSnackbar(response?.data?.message ?? "Export started — check your email shortly.", {
				variant: "success",
			});
		} catch (err) {
			console.error(err);
			enqueueSnackbar("Error while starting postal bounces export.", { variant: "error" });
		} finally {
			setExportLoading(false);
		}
	};

	const TableTopRow = () => {
		return (
			<Box
				sx={{
					width: "100%",
					overflowX: "auto",
					py: 0.5,
					WebkitOverflowScrolling: "touch",
					"&::-webkit-scrollbar": { display: "none" },
				}}
			>
				<Stack direction={"row"} spacing={2} alignItems={"center"} sx={{ display: "inline-flex" }}>
					<ToggleButtonGroup
						color="primary"
						value={environment}
						exclusive
						onChange={handleEnvironmentChange}
						size="small"
						sx={{ whiteSpace: "nowrap", display: "inline-flex" }}
					>
						<ToggleButton value="all">All Envs</ToggleButton>
						<ToggleButton value="prod">Prod</ToggleButton>
						<ToggleButton value="staging">Staging</ToggleButton>
						<ToggleButton value="dev">Dev</ToggleButton>
					</ToggleButtonGroup>

					<FormControl size="small" sx={{ minWidth: 150 }}>
						<InputLabel id="traffic-type-label">Traffic</InputLabel>
						<Select
							labelId="traffic-type-label"
							label="Traffic"
							value={trafficType}
							onChange={(event) => {
								setTrafficType(event.target.value as TrafficTypeFilter);
								setPage(1);
							}}
						>
							<MenuItem value="all">All Traffic</MenuItem>
							<MenuItem value="campaign">Campaign</MenuItem>
							<MenuItem value="warmup">Warmup</MenuItem>
							<MenuItem value="test">Test</MenuItem>
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 170 }}>
						<InputLabel id="event-type-label">Event Type</InputLabel>
						<Select
							labelId="event-type-label"
							label="Event Type"
							value={eventType}
							onChange={(event) => {
								setEventType(event.target.value as EventTypeFilter);
								setPage(1);
							}}
						>
							<MenuItem value="all">All Events</MenuItem>
							<MenuItem value="bounced">Bounced</MenuItem>
							<MenuItem value="hard_fail">Delivery Failed</MenuItem>
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 140 }}>
						<InputLabel id="days-label">Time Range</InputLabel>
						<Select
							labelId="days-label"
							label="Time Range"
							value={days}
							onChange={(event) => {
								setDays(Number(event.target.value));
								setPage(1);
							}}
						>
							<MenuItem value={1}>Last 24 hours</MenuItem>
							<MenuItem value={7}>Last 7 days</MenuItem>
							<MenuItem value={30}>Last 30 days</MenuItem>
							<MenuItem value={90}>Last 90 days</MenuItem>
						</Select>
					</FormControl>

						<Button
							startIcon={<Downloading />}
							variant="contained"
							color="primary"
							onClick={() => {
								handleExportClick();
							}}
							disabled={exportLoading}
						>
							{exportLoading ? "Exporting..." : "Export"}
						</Button>
					</Stack>
				</Box>
			);
	};

	const columnCustomizerLabel = {
		recipient: "Recipient",
		sender: "Sender",
		subject: "Subject",
		environment: "Environment",
		traffic_type: "Traffic Type",
		event_type: "Event Type",
		reason_type: "Reason Type",
		reason_code: "Code",
		output: "SMTP Output",
		user_email: "User Email",
		workspace_name: "Workspace",
		timestamp_ms: "Bounced On",
	};

	const columnDefs: ColDef[] = [
		{
			field: "recipient",
			headerName: "Recipient",
			cellRenderer: RecipientLink,
			minWidth: 220,
			flex: 2,
		},
		{ field: "sender", headerName: "Sender", minWidth: 220, flex: 2 },
		{ field: "subject", headerName: "Subject", valueFormatter: emptyValueFormatter, flex: 2 },
		{ field: "environment", headerName: "Environment", cellRenderer: EnvironmentBadge, width: 140 },
		{ field: "traffic_type", headerName: "Traffic Type", cellRenderer: TrafficTypeBadge, width: 140 },
		{ field: "event_type", headerName: "Event Type", cellRenderer: EventTypeBadge, width: 160 },
		{ field: "reason_type", headerName: "Reason Type", minWidth: 180 },
		{ field: "reason_code", headerName: "Code", width: 120 },
		{ field: "output", headerName: "SMTP Output", cellRenderer: OutputCell, minWidth: 260, flex: 2 },
		{ field: "user_email", headerName: "User Email", cellRenderer: UserDetailsLink, minWidth: 220 },
		{ field: "workspace_name", headerName: "Workspace", valueFormatter: emptyValueFormatter },
		{
			field: "timestamp_ms",
			headerName: "Bounced On",
			valueFormatter: timestampFormatter,
			minWidth: 200,
		},
	];

	if (!pageData && adminPostalBouncesQuery.isLoading) {
		return <PageLoading />;
	} else if (adminPostalBouncesQuery.error as unknown as ApiRequestFailed) {
		return (
			<PageDataErrorHandler
				error={adminPostalBouncesQuery.error as unknown as ApiRequestFailed}
			/>
		);
	} else if (pageData) {
		return (
			<Stack
				direction={"column"}
				spacing={2}
				sx={{ pb: 4, height: "100%", width: "100%", maxWidth: "100vw", overflowX: "hidden" }}
			>
				<Stack direction={"row"} spacing={2} alignItems={"center"}>
					<Typography fontWeight={"bold"} variant={"h5"}>
						Postal Bounces
					</Typography>
				</Stack>
				<Divider />

				{serverErrors.length > 0 && (
					<Alert severity="warning">
						{serverErrors.length} mail server(s) could not be read, so these results are
						incomplete: {serverErrors.map((e) => `${e.server} (${e.error})`).join("; ")}
					</Alert>
				)}

				<Stack direction={"row"} spacing={2} sx={{ flexWrap: "wrap", gap: 2 }}>
					<Box sx={{ flex: "1 1 200px" }}>
						<StatCard
							icon={<ReportProblem />}
							heading={"Total"}
							value={pageData.total_count}
							color={"error.main"}
						/>
					</Box>
					<Box sx={{ flex: "1 1 200px" }}>
						<StatCard
							icon={<MarkEmailUnread />}
							heading={"Bounced"}
							value={totals.bounced ?? 0}
							color={"error.main"}
						/>
					</Box>
					<Box sx={{ flex: "1 1 200px" }}>
						<StatCard
							icon={<LocalFireDepartment />}
							heading={"Delivery Failed"}
							value={totals.hard_fail ?? 0}
							color={"warning.main"}
						/>
					</Box>
					<Box sx={{ flex: "1 1 200px" }}>
						<StatCard
							icon={<Whatshot />}
							heading={"Warmup Bounces"}
							value={totals.warmup ?? 0}
							color={"warning.main"}
						/>
					</Box>
				</Stack>

				<Box sx={{ height: "100%", width: "100%", minWidth: 0, flexGrow: 1 }}>
					<PaginatedServerSideDataTable
						columns={columnDefs}
						paginationData={pageData}
						loading={
							adminPostalBouncesQuery.isLoading || adminPostalBouncesQuery.isFetching
						}
						onPageChange={handlePageChange}
						onPageSizeChange={handlePageSizeChange}
						onSearchChange={handleSearchChange}
						searchPlaceholder="Search recipient, sender, subject, workspace, reason..."
						noRowsText="No bounces found for the selected filters."
						actions={<TableTopRow />}
						columnLabels={columnCustomizerLabel}
					/>
				</Box>
			</Stack>
		);
	} else {
		// Ideally it should not reach here.
		return <></>;
	}
}
